/**
 * Leitura de variável de ambiente SEM fallback de credencial.
 *
 * Regra do CLAUDE.md, seção "Regras inegociáveis":
 *   "Nunca usar fallback de credencial no código (process.env.X || 'valor').
 *    Falta de variável deve impedir a aplicação de subir."
 *
 * Este arquivo existe porque `supabase-service.ts` trazia url e key literais
 * dentro de um array `configs` — o mesmo array que fazia o `.env.example` antigo
 * apontar para um projeto Supabase DIFERENTE do que o runtime usava, sem que
 * ninguém entendesse por que os dados não apareciam (ver docs/NYROH.md, §3).
 *
 * COMO USAR — sempre passando o VALOR, nunca só o nome:
 *
 *     const url = exigirEnv(
 *       'VITE_SUPABASE_URL',
 *       import.meta.env.VITE_SUPABASE_URL,
 *       'URL do gateway que o painel consulta',
 *     );
 *
 * O valor é passado junto de propósito. O Vite substitui `import.meta.env.VITE_X`
 * estaticamente no build; um acesso dinâmico (`import.meta.env[nome]`) depende do
 * objeto inteiro sobreviver ao bundle e é frágil. Passando o valor, a substituição
 * estática continua garantida.
 *
 * Só variáveis com prefixo `VITE_` chegam ao navegador. As demais existem apenas
 * no processo Node (`server.ts`, `api/**`) — ver nyroh-leads/.env.example.
 */

const PROJETO = 'nyroh-leads';

function montarMensagem(nome: string, paraQue: string): string {
  return [
    `[${PROJETO}] Variável de ambiente ausente: ${nome}`,
    ``,
    `Para que serve: ${paraQue}`,
    ``,
    `Como resolver:`,
    `  1. cp .env.example .env.local`,
    `  2. Ambiente local — na raiz do repositório: docker compose up -d`,
    `     O .env.example já vem com os valores locais preenchidos.`,
    `  3. Nuvem — preencha com os seus valores. Nunca commite o .env.`,
    ``,
    `Por que não existe valor padrão: o CLAUDE.md proíbe fallback de credencial`,
    `no código. Sem a variável a aplicação não sobe — isso é proposital.`,
  ].join('\n');
}

/**
 * Torna o erro visível na tela, e não só no console.
 *
 * Sem isto, a exceção lançada durante o import deixa a página em branco e a
 * causa fica escondida no DevTools — que é o oposto de "falhar alto e cedo".
 */
function avisarNaTela(texto: string): void {
  if (typeof document === 'undefined') return;

  const pintar = (): void => {
    if (document.getElementById('nyroh-env-erro')) return;

    const caixa = document.createElement('pre');
    caixa.id = 'nyroh-env-erro';
    caixa.textContent = texto;
    caixa.setAttribute(
      'style',
      [
        'position:fixed',
        'inset:0',
        'z-index:2147483647',
        'margin:0',
        'padding:32px',
        'overflow:auto',
        'background:#0D1117',
        'color:#E6EAF0',
        'font:13px/1.6 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
        'white-space:pre-wrap',
        'word-break:break-word',
      ].join(';'),
    );
    document.body.appendChild(caixa);
  };

  if (document.body) {
    pintar();
  } else {
    document.addEventListener('DOMContentLoaded', pintar, { once: true });
  }
}

/**
 * Devolve o valor da variável, ou derruba a aplicação com uma mensagem acionável.
 *
 * @param nome    Nome exato da variável, para a mensagem de erro.
 * @param valor   O valor lido — passe `import.meta.env.VITE_X` diretamente.
 * @param paraQue Uma linha explicando o papel dela, mostrada no erro.
 */
export function exigirEnv(nome: string, valor: string | undefined, paraQue: string): string {
  const limpo = typeof valor === 'string' ? valor.trim() : '';
  if (limpo !== '') return limpo;

  const texto = montarMensagem(nome, paraQue);
  avisarNaTela(texto);
  throw new Error(texto);
}
